/**
 * Transform utilities for converting between Zustand store format and API format
 * Keeps the transformation logic clean and centralized
 */

import { DeliveryModeV2 } from '@/types/program-v2';

// ============================================================================
// ZUSTAND → API FORMAT (for saving drafts)
// ============================================================================

/** Step-1 / common program fields — use for edit saves so fields are not dropped from payloads. */
export function pickProgramCommonFieldsForSave(storeData: {
  title?: string;
  slug?: string;
  navThumbnailUrl?: string | null;
  categoryId?: string | { _id?: string } | null;
  duration?: unknown;
  theoryDuration?: unknown;
  language?: string;
  status?: string;
  isPublished?: boolean;
  platform?: unknown;
  socialMedia?: unknown;
  whoisthisFor?: unknown;
  wherecaniWork?: unknown;
  clinicalProceduresGrouped?: unknown;
  hasclinical?: boolean;
  clinicalsScheduling?: {
    schedulingWindowDays: number;
    reactivationFeeCents: number;
    rescheduleFeeFirstMissCents: number;
    rescheduleFeeSecondMissCents: number;
    rescheduleFeeThirdMissCents: number;
    timezone: string;
    formVariant?: string;
  } | null;
  certificationExamScheduling?: {
    onCampusUrl?: string;
    onlineUrl?: string;
  } | null;
  modules?: unknown;
  premiumShareCount?: number;
  marketingTags?: string[];
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
}) {
  const categoryId =
    typeof storeData.categoryId === "object" && storeData.categoryId !== null
      ? (storeData.categoryId as { _id?: string })._id
      : storeData.categoryId;

  return {
    title: storeData.title,
    slug: storeData.slug,
    navThumbnailUrl: storeData.navThumbnailUrl ?? null,
    categoryId,
    duration: storeData.duration,
    theoryDuration: storeData.theoryDuration,
    language: storeData.language,
    status: storeData.status,
    isPublished: storeData.isPublished,
    platform: storeData.platform,
    socialMedia: storeData.socialMedia,
    whoisthisFor: storeData.whoisthisFor,
    wherecaniWork: storeData.wherecaniWork,
    clinicalProceduresGrouped: storeData.clinicalProceduresGrouped,
    hasclinical: storeData.hasclinical,
    clinicalsScheduling: storeData.hasclinical ? storeData.clinicalsScheduling : undefined,
    certificationExamScheduling: storeData.certificationExamScheduling ?? {
      onCampusUrl: "",
      onlineUrl: "",
    },
    modules: storeData.modules,
    premiumShareCount: storeData.premiumShareCount,
    marketingTags: storeData.marketingTags ?? [],
    premiumDescription: storeData.premiumDescription,
    premiumTagline: storeData.premiumTagline,
    premiumRating: storeData.premiumRating,
    premiumReviewCount: storeData.premiumReviewCount,
    premiumStudentCount: storeData.premiumStudentCount,
    premiumViewCount: storeData.premiumViewCount,
    premiumLikeCount: storeData.premiumLikeCount,
  };
}

export function transformToAPIFormat(storeData: any) {
  // Extract program types from the store format
  const programTypes = Object.entries(storeData.programTypes || {}).map(([type, data]) => ({
    type,
    ...(data as any),
  }));

  const programData = pickProgramCommonFieldsForSave(storeData);

  return {
    programData,
    programTypes,
  };
}

// ============================================================================
// API → ZUSTAND FORMAT (for loading drafts)
// ============================================================================

export function transformFromAPIFormat(apiData: any) {
  const { programData, programTypes, draftId, lastSaved } = apiData;

  // Convert programTypes array back to object format
  const programTypesObj: Record<DeliveryModeV2, any> = {} as any;
  
  programTypes?.forEach((pt: any) => {
    const { type, ...rest } = pt;
    programTypesObj[type as DeliveryModeV2] = rest;
  });

  // Determine active program type (first one in the list, or hybrid as fallback)
  const activeProgramType = (programTypes?.[0]?.type as DeliveryModeV2) || 'hybrid';

  return {
    // Step 1 fields
    title: programData?.title || '',
    slug: programData?.slug || '',
    navThumbnailUrl: programData?.navThumbnailUrl ?? null,
    categoryId: programData?.categoryId || '',
    duration: programData?.duration, // do not default
    theoryDuration: programData?.theoryDuration, // do not default
    language: programData?.language || 'english',
    status: programData?.status || 'active',
    isPublished: programData?.isPublished || false,
    platform: programData?.platform || { isIspring: false },
    socialMedia: programData?.socialMedia || {},
    whoisthisFor: programData?.whoisthisFor || [],
    wherecaniWork: programData?.wherecaniWork || [],
    clinicalProceduresGrouped: programData?.clinicalProceduresGrouped || [],
    hasclinical: programData?.hasclinical || false,
    clinicalsScheduling: programData?.clinicalsScheduling
      ? {
          schedulingWindowDays: programData.clinicalsScheduling.schedulingWindowDays ?? 30,
          reactivationFeeCents: programData.clinicalsScheduling.reactivationFeeCents ?? 5000,
          rescheduleFeeFirstMissCents:
            programData.clinicalsScheduling.rescheduleFeeFirstMissCents ??
            programData.clinicalsScheduling.rescheduleFeeCents ??
            2500,
          rescheduleFeeSecondMissCents:
            programData.clinicalsScheduling.rescheduleFeeSecondMissCents ?? 4500,
          rescheduleFeeThirdMissCents:
            programData.clinicalsScheduling.rescheduleFeeThirdMissCents ?? 7500,
          timezone: programData.clinicalsScheduling.timezone || "America/New_York",
          formVariant: programData.clinicalsScheduling.formVariant || "standard_v1",
        }
      : {
      schedulingWindowDays: 30,
      reactivationFeeCents: 5000,
      rescheduleFeeFirstMissCents: 2500,
      rescheduleFeeSecondMissCents: 4500,
      rescheduleFeeThirdMissCents: 7500,
      timezone: "America/New_York",
      formVariant: "standard_v1",
    },
    certificationExamScheduling: {
      onCampusUrl: programData?.certificationExamScheduling?.onCampusUrl || "",
      onlineUrl: programData?.certificationExamScheduling?.onlineUrl || "",
    },
    modules: programData?.modules || [],
    premiumShareCount: programData?.premiumShareCount || 0,
    flyerTheme: programData?.flyerTheme ?? 'black',
    marketingTags: Array.isArray(programData?.marketingTags)
      ? programData.marketingTags.filter((t: unknown) => typeof t === "string" && t.trim())
      : [],
    premiumDescription: programData?.premiumDescription || '',
    premiumTagline: programData?.premiumTagline || '',
    premiumRating: programData?.premiumRating || 0,
    premiumReviewCount: programData?.premiumReviewCount || 0,
    premiumStudentCount: programData?.premiumStudentCount || 0,
    premiumViewCount: programData?.premiumViewCount || 0,
    premiumLikeCount: programData?.premiumLikeCount || 0,
    
    // Step 2 fields
    activeProgramType,
    programTypes: programTypesObj,
    
    // Draft metadata
    draftId,
    lastSaved: lastSaved ? new Date(lastSaved) : null,
  };
}




